import {
  Button,
  Modal,
  Form,
  Select,
  Row,
  Col,
  Input,
  InputNumber,
  DatePicker,
  Space,
} from "antd";
import { ClearOutlined, FilterOutlined } from "@ant-design/icons";
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import MongerlabCustomerService from "../../services/MongerlabCustomerService";
import { can } from "../../utils";

const { Item } = Form;
const { RangePicker } = DatePicker;

export interface FilterHandle {
  resetForm: () => void;
}

interface FilterProps {
  onChangeFilter: (query: string, fromDetailed?: boolean) => void;
  onClearAll?: () => void;
  setFilterOptions: (options: any[]) => void;
}

const Filter = forwardRef<FilterHandle, FilterProps>(
  ({ onChangeFilter, onClearAll, setFilterOptions }, ref) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [form] = Form.useForm();
    const [crmStatusOptions, setCrmStatusOptions] = useState<any[]>([]);
    const [roleOptions, setRoleOptions] = useState<any[]>([]);
    const [appStatusOptions, setAppStatusOptions] = useState<any[]>([]);

    useImperativeHandle(ref, () => ({
      resetForm: () => form.resetFields(),
    }));

    useEffect(() => {
      if (
        can("mongerlab-customers:crm-statuses:getAll") ||
        can("mongerlab-customer-statuses:getAll")
      ) {
        MongerlabCustomerService.getCrmStatuses().then((r) =>
          setCrmStatusOptions(r.data.map((i) => ({ value: i.id, label: i.name })))
        );
      }
      if (can("mongerlab-customers:roles:getAll") || can("mongerlab-customer-roles:getAll")) {
        MongerlabCustomerService.getRoles().then((r) =>
          setRoleOptions(r.data.map((i) => ({ value: i.id, label: i.name })))
        );
      }
      if (
        can("mongerlab-customers:app-statuses:getAll") ||
        can("mongerlab-customer-app-statuses:getAll")
      ) {
        MongerlabCustomerService.getAppStatuses().then((r) =>
          setAppStatusOptions(r.data.map((i) => ({ value: i.id, label: i.name })))
        );
      }
    }, []);

    const buildQuery = (values: any) => {
      const params: string[] = [];
      const options: any[] = [];

      const addParam = (key: string, value: any, label: string) => {
        if (value !== undefined && value !== null && value !== "") {
          params.push(`${key}=${encodeURIComponent(value)}`);
          options.push({ key, label, value });
        }
      };

      addParam("roleId", values.roleId, `Role: ${values.roleId}`);
      addParam("crmStatusId", values.crmStatusId, `CRM Status: ${values.crmStatusId}`);
      addParam("appStatusId", values.appStatusId, `App Status: ${values.appStatusId}`);
      addParam("subsPlan", values.subsPlan, `Plan: ${values.subsPlan}`);
      addParam("productQuantity", values.productQuantity, `Product Qty: ${values.productQuantity}`);
      addParam("userAccountCount", values.userAccountCount, `Account Count: ${values.userAccountCount}`);

      if (values.isDeleted !== undefined && values.isDeleted !== null) {
        addParam("isDeleted", values.isDeleted, `Deleted: ${values.isDeleted}`);
      }

      if (values.registerDateRange?.length === 2) {
        addParam("registerDateGt", values.registerDateRange[0].toISOString(), "Register From");
        addParam("registerDateLte", values.registerDateRange[1].toISOString(), "Register To");
      }
      if (values.lastLoginDateRange?.length === 2) {
        addParam("lastLoginDateGt", values.lastLoginDateRange[0].toISOString(), "Last Login From");
        addParam("lastLoginDateLte", values.lastLoginDateRange[1].toISOString(), "Last Login To");
      }

      setFilterOptions(options);
      return params.length ? `&${params.join("&")}` : "";
    };

    const onFinish = (values: any) => {
      const query = buildQuery(values);
      onChangeFilter(query, true);
      setIsModalOpen(false);
    };

    const handleClear = () => {
      form.resetFields();
      setFilterOptions([]);
      onChangeFilter("");
      localStorage.removeItem("mongerlabCustomerFilterText");
      onClearAll?.();
    };

    return (
      <>
        <Space wrap>
          <Button
            type="default"
            icon={<FilterOutlined />}
            onClick={() => setIsModalOpen(true)}
          >
            Detaylı Filtre
          </Button>
          <Button type="text" danger icon={<ClearOutlined />} onClick={handleClear}>
            Filtreyi Temizle
          </Button>
        </Space>

        <Modal
          title="Mongerlab Customers — Detaylı Filtre"
          open={isModalOpen}
          onCancel={() => setIsModalOpen(false)}
          width={800}
          footer={[
            <Button key="clear" onClick={handleClear}>
              Filtreyi Temizle
            </Button>,
            <Button key="cancel" onClick={() => setIsModalOpen(false)}>
              İptal
            </Button>,
            <Button key="submit" type="primary" onClick={() => form.submit()}>
              Uygula
            </Button>,
          ]}
        >
          <Form form={form} layout="vertical" onFinish={onFinish}>
            <Row gutter={16}>
              {(can("mongerlab-customers:roles:getAll") || can("mongerlab-customer-roles:getAll")) && (
                <Col span={12}>
                  <Item name="roleId" label="Role">
                    <Select allowClear options={roleOptions} showSearch optionFilterProp="label" />
                  </Item>
                </Col>
              )}
              {(can("mongerlab-customers:crm-statuses:getAll") ||
                can("mongerlab-customer-statuses:getAll")) && (
                <Col span={12}>
                  <Item name="crmStatusId" label="CRM Status">
                    <Select allowClear options={crmStatusOptions} showSearch optionFilterProp="label" />
                  </Item>
                </Col>
              )}
              {(can("mongerlab-customers:app-statuses:getAll") ||
                can("mongerlab-customer-app-statuses:getAll")) && (
                <Col span={12}>
                  <Item name="appStatusId" label="App Status">
                    <Select allowClear options={appStatusOptions} showSearch optionFilterProp="label" />
                  </Item>
                </Col>
              )}
              <Col span={12}>
                <Item name="subsPlan" label="Subscription Plan">
                  <Input allowClear />
                </Item>
              </Col>
              <Col span={12}>
                <Item name="productQuantity" label="Product Quantity">
                  <InputNumber style={{ width: "100%" }} min={0} />
                </Item>
              </Col>
              <Col span={12}>
                <Item name="userAccountCount" label="User Account Count">
                  <InputNumber style={{ width: "100%" }} min={0} />
                </Item>
              </Col>
              <Col span={12}>
                <Item name="isDeleted" label="Deleted">
                  <Select
                    allowClear
                    options={[
                      { value: "false", label: "Active only" },
                      { value: "true", label: "Deleted only" },
                    ]}
                  />
                </Item>
              </Col>
              <Col span={12}>
                <Item name="registerDateRange" label="Register Date">
                  <RangePicker showTime style={{ width: "100%" }} />
                </Item>
              </Col>
              <Col span={12}>
                <Item name="lastLoginDateRange" label="Last Login Date">
                  <RangePicker showTime style={{ width: "100%" }} />
                </Item>
              </Col>
            </Row>
          </Form>
        </Modal>
      </>
    );
  }
);

Filter.displayName = "MongerlabCustomerFilter";

export default Filter;
